// IBAN/BIC-Prüfung für die Überweisungsdaten einer VA (Rubrik Zahlung)
//
// Zwei grundverschiedene Prüfungen, weil die beiden Formate unterschiedlich viel
// eingebaute Sicherheit mitbringen:
//
//   * Die IBAN trägt zwei Prüfziffern (Stellen 3+4) nach ISO 7064 (MOD 97-10) —
//     ein Tippfehler oder zwei vertauschte Ziffern lassen sich damit tatsächlich
//     ERKENNEN, nicht nur eine Formvorschrift einhalten.
//   * Die BIC (ISO 9362) hat KEINE Prüfziffer. Eine "BIC-Validierung" kann daher nur
//     die Form prüfen (Länge, welche Stellen Buchstaben vs. alphanumerisch sein
//     müssen) — ein Zahlendreher in einer echten BIC bleibt unentdeckt, wenn er
//     zufällig wieder auf ein gültiges Muster fällt. Das ist eine Eigenschaft des
//     BIC-Formats, keine Lücke dieser Funktion.
//
// Beide Funktionen sind bewusst reine Formatprüfungen ohne Netzwerkzugriff: eine
// echte Existenzprüfung (Bankleitzahlenverzeichnis, IBAN-Rechner-API) wäre die
// gründlichere Antwort, ist aber ein anderer Aufwand als "kleiner Validator".

/** Entfernt Leerzeichen und schreibt groß — wie IBAN/BIC üblicherweise eingegeben werden. */
export function normalizeIban(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase();
}

export function normalizeBic(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase();
}

/**
 * Bekannte IBAN-Längen je Länderkennung (SEPA-Raum + gängige Drittländer).
 *
 * Nicht vollständig (es gibt über 80 IBAN-Länder), aber deckt die Länder ab, aus
 * denen bei Jacques realistisch überwiesen wird. Eine unbekannte Länderkennung
 * fällt auf die generische Längenprüfung (15–34) zurück, statt eine an sich
 * plausible IBAN abzulehnen, nur weil ihr Land hier fehlt.
 */
const IBAN_LENGTH_BY_COUNTRY: Record<string, number> = {
  AT: 20, BE: 16, CH: 21, CZ: 24, DE: 22, DK: 18, ES: 24, FI: 18,
  FR: 27, GB: 22, IE: 22, IT: 27, LI: 21, LU: 20, NL: 18, NO: 15,
  PL: 28, PT: 25, SE: 24, SK: 24,
};

const IBAN_PATTERN = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;

/**
 * Mod-97-Prüfsumme nach ISO 7064 — der eigentliche Prüfziffern-Algorithmus der IBAN.
 *
 * Die ersten vier Stellen (Länderkennung + Prüfziffern) wandern ans Ende, Buchstaben
 * werden zu Zahlen (A=10 … Z=35), und das Ergebnis muss beim Teilen durch 97 den Rest
 * 1 ergeben. Da die Zahl weit über das hinausgeht, was IEEE-754 verlustfrei trägt,
 * wird sie schrittweise verarbeitet (Standardtechnik für Prüfsummen über sehr lange
 * Ziffernfolgen) statt als eine einzige große Zahl.
 */
function mod97Checksum(rearranged: string): number {
  let remainder = 0;

  for (const char of rearranged) {
    const digit = char >= '0' && char <= '9' ? char.charCodeAt(0) - 48 : char.charCodeAt(0) - 55;
    // charCodeAt('A') - 55 === 10, … 'Z' - 55 === 35 — genau die ISO-7064-Abbildung.
    remainder = digit > 9 ? (remainder * 100 + digit) % 97 : (remainder * 10 + digit) % 97;
  }

  return remainder;
}

/**
 * Ist dieser Text eine plausible IBAN?
 *
 * Prüft Form UND Prüfsumme — anders als isValidBic() unten, wo es keine Prüfsumme
 * gibt. Leer gilt als gültig: das Feld ist optional, eine leere Eingabe ist kein
 * Fehler, den diese Funktion melden soll (das entscheidet die Formularpflicht, nicht
 * der Validator).
 */
export function isValidIban(raw: string): boolean {
  const iban = normalizeIban(raw);
  if (!iban) return true;

  if (!IBAN_PATTERN.test(iban)) return false;

  const country = iban.slice(0, 2);
  const expectedLength = IBAN_LENGTH_BY_COUNTRY[country];
  if (expectedLength !== undefined && iban.length !== expectedLength) return false;

  const rearranged = iban.slice(4) + iban.slice(0, 4);
  return mod97Checksum(rearranged) === 1;
}

/**
 * Ist dieser Text eine plausible BIC?
 *
 * NUR eine Formprüfung (siehe Dateikopf) — 8 oder 11 Zeichen: 4 Buchstaben
 * Bankcode, 2 Buchstaben Länderkennung (ISO 3166-1 alpha-2), 2 alphanumerische
 * Stellen Ortscode, optional 3 alphanumerische Stellen Filialcode.
 */
export function isValidBic(raw: string): boolean {
  const bic = normalizeBic(raw);
  if (!bic) return true;

  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic);
}
